import { Replay } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslate } from "react-admin";

import { borderRadius, color, space } from "../../../theme/designTokens";

type DialogueStep = {
  id: string;
  speaker: string;
  said: string;
  feeling?: string;
  response?: string;
};

type DialogueBlockProps = {
  steps: DialogueStep[];
  autoAdvanceMs?: number;
};

export const DialogueBlock = ({ steps, autoAdvanceMs }: DialogueBlockProps) => {
  const translate = useTranslate();
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [stepIndex, setStepIndex] = useState(0);
  const lastIndex = steps.length - 1;
  const step = steps[stepIndex];

  useEffect(() => {
    if (reducedMotion || !autoAdvanceMs || autoAdvanceMs <= 0 || stepIndex >= lastIndex) return undefined;
    const timer = window.setTimeout(() => setStepIndex(current => Math.min(current + 1, lastIndex)), autoAdvanceMs);
    return () => window.clearTimeout(timer);
  }, [autoAdvanceMs, lastIndex, reducedMotion, stepIndex]);

  return (
    <Stack spacing={space.xl}>
      <Typography aria-live="polite" color="text.secondary">
        {translate("learning.dialogue.progress", { current: stepIndex + 1, total: steps.length })}
      </Typography>
      <Box
        data-testid="learning-dialogue-current"
        sx={{ p: space.xxl, borderRadius: borderRadius.md, backgroundColor: color.selectedOptionBackground }}
        aria-live="polite"
      >
        <Stack spacing={space.md}>
          <Stack direction="row" gap={space.md} alignItems="center" flexWrap="wrap">
            <Typography component="h3" variant="h6">
              {step.speaker}
            </Typography>
            {step.feeling && <Chip size="small" label={step.feeling} />}
          </Stack>
          <Typography variant="h6">„{step.said}“</Typography>
          {step.response && (
            <Typography color="text.secondary">
              {translate("learning.dialogue.response")}: {step.response}
            </Typography>
          )}
        </Stack>
      </Box>
      {stepIndex < lastIndex ? (
        <Button variant="contained" onClick={() => setStepIndex(current => Math.min(current + 1, lastIndex))}>
          {translate("learning.dialogue.next")}
        </Button>
      ) : (
        <Button startIcon={<Replay />} onClick={() => setStepIndex(0)}>
          {translate("learning.dialogue.replay")}
        </Button>
      )}
      <Accordion disableGutters elevation={0} sx={{ border: `1px solid ${color.panelBorder}` }}>
        <AccordionSummary>{translate("learning.dialogue.transcript")}</AccordionSummary>
        <AccordionDetails>
          <Stack spacing={space.md}>
            {steps.map(item => (
              <Typography key={item.id}>
                <strong>{item.speaker}:</strong> {item.said}
                {item.response ? ` — ${item.response}` : ""}
              </Typography>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};
